import React, { useMemo } from 'react';
import size from 'lodash/size';
import map from 'lodash/map';

import { ErrorMessage, ID, IsFetched } from '../../../../../types';

import {
  UpdateMaterialsSetCacheAction,
  FetchMaterialsSetsCacheKeys
} from '../../../../materialsSets/materialsSetsTypes';
import { MaterialsListOnMaterialsSelect } from '../../../../materials/components/lists/MaterialsList';

import {
  OnSelectedMaterialsSidebarCloseAction,
  FetchMaterialsCacheKeys
} from '../../../../materials/materialsTypes';

import { SelectedMaterialsSidebarMaterialsSet } from './SelectedMaterialsSidebar.types';

import { useTableCheckable } from '../../../../../common/hooks/useTableCheckable';
import { useCurrentUser } from '../../../../../auth/hooks/useAuth';

import { SelectedLibraryItemsSidebar } from '../../../../common/components/sidebars/SelectedLibraryItemsSidebar';
import { SelectedMaterialsSidebarFooter } from './components/SelectedMaterialsSidebarFooter';

import {
  SelectedMaterialsList,
  SelectedMaterialsListMaterialsSetSelectedMaterials
} from '../../lists/SelectedMaterialsList';

import { SelectedMaterialsPermissions } from '../../../selectedMaterialsConstants';

import { materialsKeys } from '../../../../../locales/keys';

interface SelectedMaterialsSidebarProps {
  materialsSet: SelectedMaterialsSidebarMaterialsSet;
  materialsSetFetched: IsFetched;
  materialsSetError: ErrorMessage;
  materialsSetIsPlaceholderData: boolean;
  materialsCacheKeys?: FetchMaterialsCacheKeys;
  materialsSetCacheKeys?: FetchMaterialsSetsCacheKeys;
  onClose: OnSelectedMaterialsSidebarCloseAction;
  onCustomAttach?: MaterialsListOnMaterialsSelect;
  updateMaterialsSetCache: UpdateMaterialsSetCacheAction<SelectedMaterialsSidebarMaterialsSet>;
  withoutBackGround?: boolean;
  disabledAttach?: boolean;
}

function SelectedMaterialsSidebar({
  materialsSet,
  materialsSetFetched,
  materialsSetError,
  materialsSetIsPlaceholderData,
  materialsCacheKeys,
  materialsSetCacheKeys,
  onClose,
  onCustomAttach,
  updateMaterialsSetCache,
  withoutBackGround,
  disabledAttach
}: SelectedMaterialsSidebarProps) {
  const currentUser = useCurrentUser();

  const selectedMaterials =
    useMemo<SelectedMaterialsListMaterialsSetSelectedMaterials>(
      () => materialsSet?.selectedMaterials || [],
      [materialsSet]
    );

  const defaultCheckedIds = useMemo<ID[]>(
    () => map(selectedMaterials, 'id'),
    [selectedMaterials]
  );

  const checkable = currentUser.hasPermissions(
    SelectedMaterialsPermissions.CHANGE_SELECTED_MATERIALS_CHECKED
  );

  const {
    checkedHash,
    checkedAll,
    handleSetCheckedIds,
    handleCheckAll,
    checkedItems
  } = useTableCheckable({
    items: selectedMaterials,
    checkedIds: defaultCheckedIds
  });

  return (
    <SelectedLibraryItemsSidebar
      i18nTitle={materialsKeys.selected}
      onClose={onClose}
      footer={
        <SelectedMaterialsSidebarFooter
          materialsSet={materialsSet}
          checkedSelectedMaterialIds={map(checkedItems, 'id')}
          onClose={onClose}
          onCustomAttach={onCustomAttach}
          updateMaterialsSetCache={updateMaterialsSetCache}
          disabledAttach={disabledAttach}
        />
      }
      selectedItemsCount={size(materialsSet.selectedMaterials)}
      withoutBackGround={withoutBackGround}
    >
      <SelectedMaterialsList
        materialsSet={materialsSet}
        materialsSetFetched={materialsSetFetched}
        materialsSetError={materialsSetError}
        materialsSetIsPlaceholderData={materialsSetIsPlaceholderData}
        materialsCacheKeys={materialsCacheKeys}
        materialsSetCacheKeys={materialsSetCacheKeys}
        onSelectedMaterialsSidebarClose={onClose}
        updateMaterialsSetCache={updateMaterialsSetCache}
        checkable={checkable}
        checkedAll={checkedAll}
        checkedHash={checkedHash}
        onCheckAll={handleCheckAll}
        onSetCheckedIds={handleSetCheckedIds}
      />
    </SelectedLibraryItemsSidebar>
  );
}

export default SelectedMaterialsSidebar;
